import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useUserPrefs } from '../contexts/UserPrefsContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Download, Trash2, Moon, Sun, BellRing, BellOff, LayoutDashboard, ListTodo, Eye, EyeOff, Sparkles, Flame, Clock, Smartphone, CheckCircle2, AlertTriangle } from 'lucide-react';
import {
  requestNotificationPermission,
  isNotificationsEnabled,
  startReminderRunner,
} from '../lib/reminders';
import { supportsWebPush, subscribeToWebPush, unsubscribeFromWebPush, isWebPushSubscribed } from '../lib/push';
import { diagnoseWidget, readWidgetSnapshot } from '../lib/widget';
import { pushWidgetForUser } from '../lib/widgetSync';
import { localNotificationsStatus, requestLocalNotificationPermission, rescheduleHabitReminders, openOSSettingsForNotifications } from '../lib/localNotifications';
import { Capacitor } from '@capacitor/core';
import { CategoriesPanel } from '../components/CategoriesPanel';

export default function Settings() {
  const { user, signOut } = useAuth();
  const { theme, toggle } = useTheme();
  const { prefs, setPref } = useUserPrefs();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const [notifEnabled, setNotifEnabled] = useState(true);
  const [browserNotif, setBrowserNotif] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [localNotif, setLocalNotif] = useState(() => {
    // Hydrate from localStorage so we don't briefly flash the "Allow" CTA
    // while the async checkPermissions() round-trips on mount.
    try {
      const cached = JSON.parse(localStorage.getItem('mfh.localnotif') || 'null');
      if (cached && typeof cached === 'object') return cached;
    } catch { /* no-op */ }
    return { display: null, granted: false, denied: false, canPrompt: false };
  });
  const [localNotifScheduled, setLocalNotifScheduled] = useState(null);
  const [localNotifBusy, setLocalNotifBusy] = useState(false);
  const [widgetDiag, setWidgetDiag] = useState(null);
  const [widgetSnap, setWidgetSnap] = useState(null);
  const [widgetBusy, setWidgetBusy] = useState(false);
  const isNative = Capacitor?.isNativePlatform?.() === true;

  const refreshLocalNotif = async () => {
    const ln = await localNotificationsStatus();
    setLocalNotif(ln);
    try {
      localStorage.setItem('mfh.localnotif', JSON.stringify({
        display: ln.display,
        granted: ln.granted,
        denied: ln.denied,
        canPrompt: ln.canPrompt,
      }));
    } catch { /* no-op */ }
  };

  const refreshWidgetDiag = async () => {
    const [diag, snap] = await Promise.all([diagnoseWidget(), readWidgetSnapshot()]);
    setWidgetDiag(diag);
    setWidgetSnap(snap);
  };

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from('profiles').select('*').eq('id', user.id).maybeSingle();
      if (data) { setDisplayName(data.display_name || ''); setNotifEnabled(data.notifications_enabled ?? true); }
      setBrowserNotif(isNotificationsEnabled());
      setPushSubscribed(await isWebPushSubscribed());
      if (Capacitor?.isNativePlatform?.()) refreshWidgetDiag();
    })();
  }, [user.id]);

  const save = async () => {
    setSaving(true);
    const { error } = await supabase.from('profiles').upsert({
      id: user.id,
      display_name: displayName.trim(),
      notifications_enabled: notifEnabled,
    });
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    toast.success('Saved');
  };

  const changeEmail = async () => {
    const next = newEmail.trim();
    if (!next || next === user.email) return;
    setEmailSaving(true);
    const { error } = await supabase.auth.updateUser({ email: next });
    setEmailSaving(false);
    if (error) { toast.error(error.message); return; }
    toast.success('Check both inboxes to confirm the change');
    setNewEmail('');
  };

  const exportData = async (fmt) => {
    const [{ data: habits }, { data: logs }, { data: categories }] = await Promise.all([
      supabase.from('habits').select('*'),
      supabase.from('habit_logs').select('*'),
      supabase.from('categories').select('*'),
    ]);
    if (fmt === 'json') {
      const blob = new Blob([JSON.stringify({ habits, logs, categories }, null, 2)], { type: 'application/json' });
      downloadBlob(blob, 'my-flow-habit.json');
    } else {
      // CSV — habits and logs as two sections
      const csv = [
        'habits',
        ['id', 'name', 'type', 'target_value', 'unit', 'color', 'icon', 'schedule_type', 'time_of_day', 'archived_at', 'created_at'].join(','),
        ...(habits || []).map((h) => [h.id, esc(h.name), h.type, h.target_value, esc(h.unit), h.color, h.icon, h.schedule_type, h.time_of_day, h.archived_at || '', h.created_at].join(',')),
        '',
        'habit_logs',
        ['id', 'habit_id', 'log_date', 'status', 'value', 'mood', 'note', 'created_at'].join(','),
        ...(logs || []).map((l) => [l.id, l.habit_id, l.log_date, l.status, l.value, esc(l.mood || ''), esc(l.note || ''), l.created_at].join(',')),
      ].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      downloadBlob(blob, 'my-flow-habit.csv');
    }
    toast.success('Exported');
  };

  const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const deleteAccount = async () => {
    if (!window.confirm('Delete all your data? This cannot be undone.')) return;
    await supabase.from('habit_logs').delete().eq('user_id', user.id);
    await supabase.from('habits').delete().eq('user_id', user.id);
    await supabase.from('categories').delete().eq('user_id', user.id);
    toast.success('All your data has been deleted');
    await signOut();
    navigate('/login', { replace: true });
  };

  return (
    <div className="space-y-8 max-w-2xl" data-testid="settings-page">
      <div>
        <p className="uppercase tracking-[0.2em] text-xs text-muted-foreground font-medium">Settings</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Your flow, your way</h1>
      </div>

      <section className="rounded-2xl border border-border bg-card/30 p-6 space-y-4">
        <h2 className="font-heading font-medium">Profile</h2>
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" value={user.email} disabled data-testid="settings-email" />
        </div>
        <div>
          <Label htmlFor="name">Display name</Label>
          <Input id="name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} data-testid="settings-name" />
        </div>
        <Button onClick={save} disabled={saving} className="rounded-full" data-testid="settings-save">{saving ? 'Saving...' : 'Save changes'}</Button>
      </section>

      <section className="rounded-2xl border border-border bg-card/30 p-6 space-y-4">
        <h2 className="font-heading font-medium">Change email</h2>
        <p className="text-xs text-muted-foreground">We&apos;ll email both the old and new address to confirm.</p>
        <div>
          <Label htmlFor="new-email">New email</Label>
          <Input
            id="new-email"
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="your.new.email@example.com"
            data-testid="settings-new-email"
          />
        </div>
        <Button
          onClick={changeEmail}
          disabled={emailSaving || !newEmail.trim() || newEmail.trim() === user.email}
          className="rounded-full"
          data-testid="settings-change-email"
        >
          {emailSaving ? 'Sending…' : 'Send confirmation'}
        </Button>
      </section>

      <section className="rounded-2xl border border-border bg-card/30 p-6 space-y-4">
        <h2 className="font-heading font-medium">Appearance</h2>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {theme === 'dark' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
            <span className="text-sm">Theme</span>
          </div>
          <Button onClick={toggle} variant="ghost" className="rounded-full" data-testid="settings-theme-toggle">
            {theme === 'dark' ? 'Switch to light' : 'Switch to dark'}
          </Button>
        </div>
      </section>

      <section className="rounded-2xl border border-border bg-card/30 p-6 space-y-4" data-testid="settings-customization">
        <h2 className="font-heading font-medium">Customization</h2>

        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-start gap-3">
            <LayoutDashboard className="h-4 w-4 mt-0.5" />
            <div>
              <div className="text-sm">Opening screen</div>
              <div className="text-xs text-muted-foreground">
                {prefs.home_screen === 'welcome'
                  ? 'Welcome dashboard (quote · top habits · stats summary)'
                  : "Today's habits (jump straight into checking off)"}
              </div>
            </div>
          </div>
          <div className="inline-flex rounded-full border border-border overflow-hidden text-xs">
            <button
              type="button"
              onClick={() => setPref('home_screen', 'today')}
              className={`px-3 py-1.5 inline-flex items-center gap-1.5 ${prefs.home_screen !== 'welcome' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
              data-testid="settings-home-today"
            >
              <ListTodo className="h-3.5 w-3.5" /> Today
            </button>
            <button
              type="button"
              onClick={() => setPref('home_screen', 'welcome')}
              className={`px-3 py-1.5 inline-flex items-center gap-1.5 ${prefs.home_screen === 'welcome' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
              data-testid="settings-home-welcome"
            >
              <LayoutDashboard className="h-3.5 w-3.5" /> Welcome
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-3 pt-3 border-t border-border">
          <div className="flex items-start gap-3">
            {prefs.today_completed_behavior === 'hide' ? <EyeOff className="h-4 w-4 mt-0.5" /> : <Eye className="h-4 w-4 mt-0.5" />}
            <div>
              <div className="text-sm">Completed habits on Today</div>
              <div className="text-xs text-muted-foreground">
                {prefs.today_completed_behavior === 'hide'
                  ? 'Hide them — keeps the list focused on what is left.'
                  : 'Move to bottom — completed items stay visible for review.'}
              </div>
            </div>
          </div>
          <div className="inline-flex rounded-full border border-border overflow-hidden text-xs">
            <button
              type="button"
              onClick={() => setPref('today_completed_behavior', 'move')}
              className={`px-3 py-1.5 ${prefs.today_completed_behavior !== 'hide' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
              data-testid="settings-completed-move"
            >
              Move to bottom
            </button>
            <button
              type="button"
              onClick={() => setPref('today_completed_behavior', 'hide')}
              className={`px-3 py-1.5 ${prefs.today_completed_behavior === 'hide' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
              data-testid="settings-completed-hide"
            >
              Hide
            </button>
          </div>
        </div>

        <p className="text-[11px] text-muted-foreground pt-1">
          Habit order on Today &amp; Habits screens is drag-to-reorder. Use the handle on the left of each row.
        </p>
      </section>

      <section className="rounded-2xl border border-border bg-card/30 p-6 space-y-4">
        <h2 className="font-heading font-medium">Notifications</h2>

        {isNative && (
          <div className="space-y-2" data-testid="settings-local-notif">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {localNotif.granted ? <BellRing className="h-4 w-4 text-primary" /> : <BellOff className="h-4 w-4" />}
                <div>
                  <div className="text-sm">Habit reminders</div>
                  <div className="text-xs text-muted-foreground">
                    {localNotif.granted
                      ? (localNotifScheduled != null
                          ? `On — ${localNotifScheduled} reminder${localNotifScheduled === 1 ? '' : 's'} scheduled.`
                          : 'On — reminder times will fire even when the app is closed.')
                      : localNotif.denied
                      ? 'Notifications are blocked in iOS Settings. Tap below to open Settings and turn them on for My Flow Habit.'
                      : 'Allow notifications so habit reminder times can fire when the app is closed.'}
                  </div>
                </div>
              </div>
              <Button
                variant="ghost"
                className="rounded-full"
                disabled={localNotifBusy}
                onClick={async () => {
                  setLocalNotifBusy(true);
                  try {
                    // If iOS has already denied, requestPermissions() does
                    // NOT re-prompt — we must send the user to iOS Settings.
                    if (localNotif.denied) {
                      const opened = await openOSSettingsForNotifications();
                      if (!opened) toast.error('Open iPhone Settings → Notifications → My Flow Habit and enable Allow Notifications.');
                      return;
                    }
                    if (!localNotif.granted) {
                      const res = await requestLocalNotificationPermission();
                      setLocalNotif((s) => ({ ...s, ...res }));
                      if (res.denied) {
                        toast.error('Permission denied. Tap "Open Settings" to allow in iPhone Settings.');
                        return;
                      }
                      if (!res.granted) {
                        toast.message(`Permission state: ${res.display || 'unknown'}`);
                        return;
                      }
                      toast.success('Notifications allowed');
                    }
                    const r = await rescheduleHabitReminders(user.id);
                    setLocalNotifScheduled(r.scheduled);
                    toast.success(r.scheduled
                      ? `Scheduled ${r.scheduled} reminder${r.scheduled === 1 ? '' : 's'}`
                      : 'No habits with reminder times yet — set one on a habit first.');
                  } catch (e) {
                    toast.error(e?.message || 'Failed');
                  } finally {
                    setLocalNotifBusy(false);
                    refreshLocalNotif();
                  }
                }}
                data-testid="settings-local-notif-button"
              >
                {localNotifBusy
                  ? '…'
                  : localNotif.denied
                  ? 'Open Settings'
                  : localNotif.granted
                  ? 'Resync'
                  : 'Allow'}
              </Button>
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              iOS status: <span className="text-foreground">{localNotif.display || 'unknown'}</span>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BellRing className="h-4 w-4" />
            <div>
              <div className="text-sm">In-browser reminders</div>
              <div className="text-xs text-muted-foreground">Notify me at each habit&apos;s reminder time while the app is open.</div>
            </div>
          </div>
          <Switch
            checked={browserNotif}
            onCheckedChange={async (v) => {
              if (v) {
                const res = await requestNotificationPermission();
                if (res === 'granted') {
                  setBrowserNotif(true);
                  startReminderRunner(user.id);
                  toast.success('Reminders enabled');
                } else if (res === 'denied') {
                  toast.error('Permission denied — enable in your browser settings');
                } else {
                  toast.message('Permission was not granted');
                }
              } else {
                toast.message('Disable in your browser site settings to fully revoke');
              }
            }}
            data-testid="settings-browser-notif-switch"
          />
        </div>

        {supportsWebPush() && (
          <div className="flex items-center justify-between pt-3 border-t border-border">
            <div className="flex items-center gap-3">
              {pushSubscribed ? <BellRing className="h-4 w-4 text-primary" /> : <BellOff className="h-4 w-4" />}
              <div>
                <div className="text-sm">Push notifications (works closed-tab)</div>
                <div className="text-xs text-muted-foreground">Server-driven reminders even when the app isn&apos;t open. Requires the Edge Function setup.</div>
              </div>
            </div>
            <Button
              variant="ghost"
              className="rounded-full"
              disabled={pushBusy}
              onClick={async () => {
                setPushBusy(true);
                try {
                  if (pushSubscribed) {
                    await unsubscribeFromWebPush();
                    setPushSubscribed(false);
                    toast.success('Unsubscribed');
                  } else {
                    await subscribeToWebPush(user.id);
                    setPushSubscribed(true);
                    toast.success('Push subscribed');
                  }
                } catch (e) {
                  toast.error(e?.message || 'Could not subscribe');
                } finally {
                  setPushBusy(false);
                }
              }}
              data-testid="settings-push-toggle"
            >
              {pushBusy ? '…' : pushSubscribed ? 'Unsubscribe' : 'Subscribe'}
            </Button>
          </div>
        )}

        <div className="flex items-center justify-between pt-3 border-t border-border">
          <div className="flex items-center gap-3">
            <BellRing className="h-4 w-4" />
            <div>
              <div className="text-sm">Master switch</div>
              <div className="text-xs text-muted-foreground">A global on/off you can store on your account.</div>
            </div>
          </div>
          <Switch checked={notifEnabled} onCheckedChange={setNotifEnabled} data-testid="settings-notif-switch" />
        </div>

        <div className="pt-3 border-t border-border space-y-3" data-testid="settings-gentle-pushes">
          <p className="uppercase tracking-[0.2em] text-[10px] text-muted-foreground font-medium">Gentle daily nudges</p>

          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-start gap-3">
              <Sparkles className="h-4 w-4 mt-0.5 text-primary" />
              <div>
                <div className="text-sm">Morning quote</div>
                <div className="text-xs text-muted-foreground">A single sentence to anchor your day. Sent once at the time you pick, in your local timezone.</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="time"
                value={prefs.daily_quote_time}
                onChange={(e) => setPref('daily_quote_time', e.target.value)}
                disabled={!prefs.daily_quote_enabled}
                className="w-28 text-sm"
                data-testid="settings-quote-time"
              />
              <Switch
                checked={prefs.daily_quote_enabled}
                onCheckedChange={(v) => setPref('daily_quote_enabled', v)}
                data-testid="settings-quote-switch"
              />
            </div>
          </div>

          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-start gap-3">
              <Flame className="h-4 w-4 mt-0.5 text-secondary" />
              <div>
                <div className="text-sm">Save my streak</div>
                <div className="text-xs text-muted-foreground">If you forgot to log a habit and your streak is at risk, we&apos;ll send one gentle reminder around 7pm — never more than once a day.</div>
              </div>
            </div>
            <Switch
              checked={prefs.streak_save_enabled}
              onCheckedChange={(v) => setPref('streak_save_enabled', v)}
              data-testid="settings-streak-save-switch"
            />
          </div>

          <p className="text-[11px] text-muted-foreground inline-flex items-center gap-1.5">
            <Clock className="h-3 w-3" />
            Your timezone is set to <span className="font-medium text-foreground">{prefs.timezone}</span>.
            Both nudges need Push notifications (above) to be subscribed.
          </p>
        </div>
      </section>

      {isNative && (
        <section className="rounded-2xl border border-border bg-card/30 p-6 space-y-4" data-testid="settings-widget-diag">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-start gap-3">
              <Smartphone className="h-4 w-4 mt-0.5" />
              <div>
                <h2 className="font-heading font-medium">Home-screen widget</h2>
                <p className="text-xs text-muted-foreground">Pick a layout for your iOS widget.</p>
              </div>
            </div>
            <Button
              variant="ghost"
              className="rounded-full"
              disabled={widgetBusy}
              onClick={async () => {
                setWidgetBusy(true);
                try {
                  await pushWidgetForUser(user.id);
                  await refreshWidgetDiag();
                  toast.success('Widget synced');
                } catch (e) {
                  toast.error(e?.message || 'Sync failed');
                } finally {
                  setWidgetBusy(false);
                }
              }}
              data-testid="settings-widget-sync"
            >
              {widgetBusy ? 'Syncing…' : 'Sync widget now'}
            </Button>
          </div>

          <div className="flex items-center justify-between flex-wrap gap-3 pt-3 border-t border-border">
            <div className="flex items-start gap-3">
              <LayoutDashboard className="h-4 w-4 mt-0.5" />
              <div>
                <div className="text-sm">Layout</div>
                <div className="text-xs text-muted-foreground">
                  {prefs.widget_style === 'quote'
                    ? "Daily quote + 1–2 top habits — a calm anchor for the day."
                    : "Today's habits — up to 4 (medium) or 8 (large)."}
                </div>
              </div>
            </div>
            <div className="inline-flex rounded-full border border-border overflow-hidden text-xs">
              <button
                type="button"
                onClick={async () => {
                  await setPref('widget_style', 'habits');
                  await pushWidgetForUser(user.id, { style: 'habits' });
                  refreshWidgetDiag();
                }}
                className={`px-3 py-1.5 inline-flex items-center gap-1.5 ${prefs.widget_style !== 'quote' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                data-testid="settings-widget-style-habits"
              >
                <ListTodo className="h-3.5 w-3.5" /> Habits
              </button>
              <button
                type="button"
                onClick={async () => {
                  await setPref('widget_style', 'quote');
                  await pushWidgetForUser(user.id, { style: 'quote' });
                  refreshWidgetDiag();
                }}
                className={`px-3 py-1.5 inline-flex items-center gap-1.5 ${prefs.widget_style === 'quote' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                data-testid="settings-widget-style-quote"
              >
                <Sparkles className="h-3.5 w-3.5" /> Quote
              </button>
            </div>
          </div>

          {widgetDiag && (
            <div className="text-xs space-y-1.5 pt-3 border-t border-border">
              <p className="uppercase tracking-[0.2em] text-[10px] text-muted-foreground font-medium">Diagnostic</p>
              <DiagRow
                ok={widgetDiag.available}
                label="Bridge plugin registered"
                detail={widgetDiag.available ? 'MyFlowHabitWidget' : (widgetDiag.reason || 'unknown')}
              />
              {widgetDiag.available && (
                <>
                  <DiagRow
                    ok={!!widgetSnap}
                    label="Snapshot accessible"
                    detail={widgetSnap
                      ? `${widgetSnap.done}/${widgetSnap.total} done · ${widgetSnap.items?.length || 0} items · style: ${widgetSnap.style || 'habits'}`
                      : 'no snapshot yet'}
                  />
                </>
              )}
            </div>
          )}
        </section>
      )}

      <CategoriesPanel />

      <section className="rounded-2xl border border-border bg-card/30 p-6 space-y-4">
        <h2 className="font-heading font-medium">Your data</h2>
        <div className="flex flex-wrap gap-2">
          <Button variant="ghost" className="rounded-full" onClick={() => exportData('json')} data-testid="settings-export-json"><Download className="h-4 w-4" />Export JSON</Button>
          <Button variant="ghost" className="rounded-full" onClick={() => exportData('csv')} data-testid="settings-export-csv"><Download className="h-4 w-4" />Export CSV</Button>
        </div>
      </section>

      <section className="rounded-2xl border border-destructive/40 bg-destructive/5 p-6 space-y-3">
        <h2 className="font-heading font-medium text-destructive">Danger zone</h2>
        <p className="text-sm text-muted-foreground">Permanently delete all habits and logs from your account.</p>
        <Button variant="destructive" className="rounded-full" onClick={deleteAccount} data-testid="settings-delete-account">
          <Trash2 className="h-4 w-4" />Delete all my data
        </Button>
      </section>
    </div>
  );
}

function DiagRow({ ok, label, detail }) {
  return (
    <div className="flex items-start gap-2">
      {ok
        ? <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-primary shrink-0" />
        : <AlertTriangle className="h-3.5 w-3.5 mt-0.5 text-destructive shrink-0" />}
      <div className="flex-1">
        <div className="text-foreground">{label}</div>
        <div className="text-[11px] text-muted-foreground break-all">{detail}</div>
      </div>
    </div>
  );
}
